// In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
// ============================================================================
// NEXORAX EMPIRE OS - DIRECT SecurePayment SETTLEMENT PORTAL
// ============================================================================
import React, { useState, useEffect } from 'react';
import { CreditCard, Shield, Landmark, ArrowUpRight, CheckCircle2, Copy, Check, Info, Activity, Wifi, RefreshCw } from 'lucide-react';

export const DirectSettlementPortal: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const [activeSegment, setActiveSegment] = useState<'DEPLOYMENT' | 'MASTERY'>('DEPLOYMENT');
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [pingRate, setPingRate] = useState(48);

  const settlementStages = [
    { id: 'AWAITING', label: 'Awaiting Handshake', statusText: 'Listening on Port 3000 Ingress Routing...', colorClass: 'text-amber-500 border-amber-500/30', bgClass: 'bg-amber-950/10' },
    { id: 'PIPELINE', label: 'Pipeline Active', statusText: 'NexoraX SSL Tunnel established with SecurePayment Core APIs...', colorClass: 'text-cyan-400 border-cyan-500/30', bgClass: 'bg-cyan-950/10' },
    { id: 'READY', label: 'Settlement Ready', statusText: 'NexoraX Levi (1%) and Tax Protocol (20%) validated.', colorClass: 'text-emerald-400 border-emerald-500/30', bgClass: 'bg-emerald-950/10' }
  ];

  // Auto cycle stages to demonstrate simulated real-time pipeline telemetry
  useEffect(() => {
    const stageInterval = setInterval(() => {
      setCurrentStageIndex((prev) => (prev + 1) % settlementStages.length);
    }, 4500);

    const pingInterval = setInterval(() => {
      setPingRate(() => Math.floor(40 + Math.random() * 15));
    }, 2000);

    return () => {
      clearInterval(stageInterval);
      clearInterval(pingInterval);
    };
  }, []);

  const handleManualRefresh = () => {
    setCurrentStageIndex((prev) => (prev + 1) % settlementStages.length);
    setPingRate(Math.floor(25 + Math.random() * 20));
  };

  // Unified absolute bank-verified coordinates and checkout links
  const SecurePaymentPaymentLinks = {
    rapidDeployment: "https://pay.SecurePayment.co/pay/9fd1aa7e-20b1-44b0-bf57-b75944dd5134",
    NexoraXMastery: "https://pay.SecurePayment.co/pay/9fd1aa7e-20b1-44b0-bf57-b75944dd5134"
  };

  const copyCoordinates = () => {
    const coords = "NexoraX Digital Ltd\nSecure Payment Provider Checking Account\nSort Code: Verified Payment Configuration\nAccount Number: Secure Billing Provider";
    navigator.clipboard.writeText(coords);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const activeStage = settlementStages[currentStageIndex];

  return (
    <div id="direct-settlement-portal" className="bg-black/90 p-5 sm:p-8 rounded-3xl border border-amber-500/30 shadow-[0_0_30px_rgba(245,158,11,0.08)] space-y-6 relative overflow-hidden">
      {/* Background Ambience Strip */}
      <div className="absolute top-0 inset-x-0 h-[1.5px] bg-gradient-to-r from-transparent via-amber-500 to-transparent animate-pulse" />

      {/* Header section with absolute corporate details */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-zinc-900 pb-5">
        <div>
          <div className="flex items-center gap-1.5 text-amber-500 font-mono text-[10px] uppercase font-extrabold tracking-widest">
            <Landmark className="w-3.5 h-3.5 animate-pulse" />
            <span>NexoraX NexoraX Settlement Layer</span>
          </div>
          <h2 className="text-lg font-sans font-black text-white uppercase tracking-wider mt-1">
            Direct SecurePayment Integration Portal
          </h2>
          <p className="text-[9px] text-zinc-500 font-mono tracking-widest uppercase mt-0.5">
            NexoraX Funds Routing Node // Tier-One Capital Settlement Channels
          </p>
        </div>

        {/* Status Indicator */}
        <div className="flex items-center gap-2 px-3 py-1 bg-amber-950/20 border border-amber-500/30 rounded-full">
          <span className="w-2 h-2 rounded-full bg-amber-500 animate-ping"></span>
          <span className="font-mono text-[9px] text-amber-500 font-bold uppercase tracking-widest">SecurePayment Node Live</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
        {/* Left Column - Product Slices and Flow Selection */}
        <div className="lg:col-span-7 flex flex-col justify-between space-y-4">
          <div className="space-y-3">
            <span className="text-[10px] font-mono font-bold text-zinc-400 uppercase tracking-widest block">
              Acknowledge Settlement Segment
            </span>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setActiveSegment('DEPLOYMENT')}
                className={`p-4 border text-left rounded-xl transition-all duration-300 ${
                  activeSegment === 'DEPLOYMENT'
                    ? 'border-amber-500 bg-amber-950/20 text-white shadow-[0_0_15px_rgba(245,158,11,0.1)]'
                    : 'border-zinc-800 bg-zinc-950/30 text-zinc-400 hover:border-zinc-700 hover:text-white'
                }`}
              >
                <div className="text-xs font-mono font-black uppercase text-amber-500">RAPID DEPLOYMENT</div>
                <div className="text-[9px] text-zinc-400 mt-1 uppercase line-clamp-1">SecurePayment Instant Setup</div>
              </button>
              <button
                type="button"
                onClick={() => setActiveSegment('MASTERY')}
                className={`p-4 border text-left rounded-xl transition-all duration-300 ${
                  activeSegment === 'MASTERY'
                    ? 'border-amber-500 bg-amber-950/20 text-white shadow-[0_0_15px_rgba(245,158,11,0.1)]'
                    : 'border-zinc-800 bg-zinc-950/30 text-zinc-400 hover:border-zinc-700 hover:text-white'
                }`}
              >
                <div className="text-xs font-mono font-black uppercase text-amber-500">NEXORAX MASTERY</div>
                <div className="text-[9px] text-zinc-400 mt-1 uppercase line-clamp-1">Ultimate Node Activation</div>
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-zinc-950 rounded-xl border border-zinc-900 space-y-2">
              <div className="flex items-center gap-1.5 text-zinc-400">
                <Info className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                <span className="font-mono text-[9px] uppercase font-bold tracking-widest text-zinc-300">Operational Notice</span>
              </div>
              <p className="text-[9px] font-mono uppercase text-zinc-500 leading-relaxed">
                By clicking proceed, you are launching the bank-verified checkout portal hosted on SecurePayment. 
                All processed transactions trigger automated 1% 3% Community Allocation splits for Charities 1, 2, and 3 
                Dunfermline/Global trust networks plus 10% Maintenance & Hosting Fee allocations instantly.
              </p>
            </div>

            <a 
              href={activeSegment === 'DEPLOYMENT' ? SecurePaymentPaymentLinks.rapidDeployment : SecurePaymentPaymentLinks.NexoraXMastery} 
              target="_blank" 
              rel="noopener noreferrer"
              className="group block w-full py-4 bg-amber-500 hover:bg-amber-400 text-black text-center font-mono text-xs font-black uppercase tracking-[0.2em] hover:shadow-[0_0_20px_rgba(245,158,11,0.3)] transition-all rounded-xl"
            >
              PROCEED TO SECURE SecurePayment CHECKOUT
            </a>

            {/* Real-time Settlement Status Component */}
            <div className="p-4 border border-zinc-900 bg-[#040406]/90 rounded-2xl relative overflow-hidden space-y-3">
              <div className="flex justify-between items-center pb-2 border-b border-zinc-900">
                <div className="flex items-center gap-2">
                  <Activity className="w-3.5 h-3.5 text-amber-500 animate-pulse" />
                  <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 font-bold">
                    Settlement Status
                  </span>
                </div>
                <button 
                  onClick={handleManualRefresh}
                  type="button" 
                  className="p-1 text-zinc-600 hover:text-white transition-colors"
                  title="Force Heartbeat Verification"
                >
                  <RefreshCw className="w-3.5 h-3.5 hover:rotate-180 transition-transform duration-500" />
                </button>
              </div>

              {/* Active Stage Panel */}
              <div className={`p-3 rounded-xl border ${activeStage.colorClass} ${activeStage.bgClass} transition-all duration-300`}>
                <div className="flex justify-between items-center">
                  <span className="font-mono text-[11px] font-black uppercase tracking-wider">
                    {activeStage.label}
                  </span>
                  <div className="flex items-center gap-1.5 font-mono text-[8px] tracking-widest uppercase text-zinc-500 font-extrabold">
                    <Wifi className="w-3.5 h-3.5 text-emerald-500/80 animate-pulse" />
                    <span>PING: {pingRate}MS</span>
                  </div>
                </div>
                <div className="text-[9.5px] font-mono uppercase text-zinc-400 mt-1.5 leading-relaxed">
                  {activeStage.statusText}
                </div>
              </div>

              {/* Quick Pipeline Breadcrumbs */}
              <div className="flex gap-2 font-mono text-[8.5px] uppercase">
                {settlementStages.map((stage, idx) => {
                  const isActive = idx === currentStageIndex;
                  return (
                    <div 
                      key={stage.id} 
                      className={`flex-1 text-center py-1 rounded border transition-all ${
                        isActive 
                          ? 'bg-amber-500/15 text-amber-500 border-amber-500/20 font-black' 
                          : 'border-zinc-900 text-zinc-600'
                      }`}
                    >
                      {stage.id}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Audit Split Details and Routing Address card */}
        <div className="lg:col-span-5 bg-zinc-950/80 border border-zinc-900 rounded-2xl p-5 flex flex-col justify-between">
          <div className="space-y-4">
            <div className="flex items-center gap-1.5 border-b border-zinc-900 pb-2">
              <Shield className="w-4 h-4 text-emerald-400" />
              <span className="font-mono text-[9px] font-black tracking-widest text-zinc-200 uppercase">
                SecurePayment Verification Audit
              </span>
            </div>

            <div className="space-y-3 font-mono text-[10px]">
              <div className="flex justify-between text-zinc-400 uppercase">
                <span>Account Provider</span>
                <span className="text-white font-bold">SecurePayment Business Banking</span>
              </div>
              <div className="flex justify-between text-zinc-400 uppercase">
                <span>Direct Settlement Range</span>
                <span className="text-white font-bold">Encrypted / Instant</span>
              </div>

              {/* Direct coordinates check */}
              <div className="bg-black border border-zinc-900 rounded-xl p-3 space-y-1">
                <div className="text-[8px] text-zinc-500 font-bold uppercase tracking-wider block">NexoraX Direct Clearing Coordinates</div>
                <div className="text-[10px] text-zinc-300 space-y-0.5 uppercase">
                  <div>Holder: NexoraX Digital Ltd</div>
                  <div>Sort Code: <span className="text-amber-500 font-bold">Verified Payment Configuration</span></div>
                  <div>Account: <span className="text-amber-500 font-bold">Secure Billing Provider</span></div>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-zinc-900 mt-4 space-y-3">
            <button
              onClick={copyCoordinates}
              className="w-full py-2.5 px-3 bg-[#0a0a0d] border border-zinc-800 hover:bg-zinc-900 text-zinc-300 font-mono text-[9.5px] uppercase font-bold tracking-wider rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer"
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  Coordinates Copied
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                  Copy Secure Payment Provider
                </>
              )}
            </button>
            <p className="text-[8.5px] text-zinc-500 font-mono text-center uppercase leading-snug">
              All transactions are processed directly via SecurePayment Instant Checkout. Secure, encrypted, and bank-verified.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
